import { z } from 'zod';
import { CuidSchema } from './crm.schemas.js';
import {
  MAX_META_MESSAGE_CHARS,
  META_CHANNEL_KINDS,
  META_PAGE_SIZE,
  META_PROVIDERS,
} from './meta.js';

/**
 * مخططات قنوات ميتا.
 */

/**
 * ربط قناة.
 *
 * الرمز يُدخَل يدويًّا لا عبر تدفّق OAuth: التدفّق يحتاج معرّف تطبيق وسرّه
 * مضبوطين ومراجعةً من ميتا، والإدخال اليدوي يجعل القناة تعمل بمجرّد أن
 * يملك المسؤول رمزًا — وهو الطريق الذي تسلكه الشركات الصغيرة فعلًا.
 */
export const ConnectChannelSchema = z
  .object({
    kind: z.enum(META_CHANNEL_KINDS),
    name: z.string().trim().min(2, 'اسم القناة مطلوب.').max(120),
    /**
     * المعرّف: معرّف الصفحة، أو معرّف رقم الواتساب.
     *
     * لدى المجيب هو `from_phone_number_id` للرقم المُرسِل. أرقامٌ في الحالتين.
     */
    externalId: z
      .string()
      .trim()
      .min(3, 'المعرّف مطلوب.')
      .max(64)
      .regex(/^\d+$/, 'المعرّف أرقام فقط.'),
    /** رمز الوصول — يُشفَّر قبل الحفظ ولا يُعاد أبدًا. */
    accessToken: z.string().trim().min(20).max(500).optional(),
    /** المزوّد — يخصّ الواتساب. غيره يبقى على ميتا مباشرةً. */
    provider: z.enum(META_PROVIDERS).default('META'),
    /** رابط واجهة المجيب، مثل https://elmujib.com/api */
    providerBaseUrl: z.string().trim().url('الرابط غير صالح.').max(300).optional(),
    /** معرّف المورّد (Vendor UID) لدى المجيب. */
    providerVendorId: z.string().trim().min(8).max(100).optional(),
  })
  .refine((v) => v.provider !== 'ELMUJIB' || (v.providerVendorId && v.providerBaseUrl), {
    message: 'المجيب يحتاج رابط الواجهة ومعرّف المورّد.',
    path: ['providerVendorId'],
  })
  .refine((v) => v.provider !== 'ELMUJIB' || v.kind === 'WHATSAPP', {
    message: 'المجيب للواتساب فقط.',
    path: ['provider'],
  });
export type ConnectChannelInput = z.infer<typeof ConnectChannelSchema>;

export const UpdateMetaChannelSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  accessToken: z.string().trim().min(20).max(500).optional(),
  providerBaseUrl: z.string().trim().url('الرابط غير صالح.').max(300).optional(),
  providerVendorId: z.string().trim().min(8).max(100).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateMetaChannelInput = z.infer<typeof UpdateMetaChannelSchema>;

export const MetaConversationQuerySchema = z.object({
  channelId: CuidSchema.optional(),
  q: z.string().trim().max(200).optional(),
  unreadOnly: z.coerce.boolean().optional(),
});
export type MetaConversationQuery = z.infer<typeof MetaConversationQuerySchema>;

export const MetaMessagesQuerySchema = z.object({
  before: CuidSchema.optional(),
  limit: z.coerce.number().int().min(1).max(META_PAGE_SIZE).default(META_PAGE_SIZE),
});
export type MetaMessagesQuery = z.infer<typeof MetaMessagesQuerySchema>;

/**
 * بدء محادثةٍ من عندنا — برقمٍ جديد أو بعميلٍ قائم.
 *
 * إمّا رقمٌ يُكتب، أو سجلٌّ يُختار فيُؤخذ رقمه. القناة تحدّد من أين تُرسَل.
 */
export const StartConversationSchema = z
  .object({
    channelId: CuidSchema,
    phone: z.string().trim().min(6, 'الرقم قصير.').max(32).optional(),
    leadId: CuidSchema.optional(),
    accountId: CuidSchema.optional(),
    /* مرشّحٌ في التوظيف — لا عميلٌ ولا محتمَل، فله بابُه. */
    candidateId: CuidSchema.optional(),
  })
  .refine((v) => Boolean(v.phone || v.leadId || v.accountId || v.candidateId), {
    message: 'أدخل رقمًا أو اختر عميلًا.',
    path: ['phone'],
  })
  .refine((v) => !(v.leadId && v.accountId), {
    message: 'اختر عميلًا محتملًا أو عميلًا، لا الاثنين.',
    path: ['accountId'],
  });
export type StartConversationInput = z.infer<typeof StartConversationSchema>;

export const SendMetaMessageSchema = z.object({
  body: z
    .string()
    .trim()
    .min(1, 'اكتب رسالة.')
    .max(MAX_META_MESSAGE_CHARS, `الرسالة أطول من ${MAX_META_MESSAGE_CHARS} حرفًا.`),
});
export type SendMetaMessageInput = z.infer<typeof SendMetaMessageSchema>;

/** تخصيص المحادثة لموظف — أو جعلها عامّة بتمرير فارغ. */
export const AssignConversationSchema = z.object({
  assigneeId: CuidSchema.nullable(),
});
export type AssignConversationInput = z.infer<typeof AssignConversationSchema>;

/** ربط محادثة بسجل — أو فكّ الربط بتمرير فارغ. */
export const LinkConversationSchema = z
  .object({
    leadId: CuidSchema.nullable().optional(),
    accountId: CuidSchema.nullable().optional(),
    candidateId: CuidSchema.nullable().optional(),
  })
  .refine((v) => !(v.leadId && v.accountId), {
    message: 'اربطها بعميل محتمل أو بعميل، لا بالاثنين.',
  })
  .refine((v) => !(v.candidateId && (v.leadId || v.accountId)), {
    message: 'المرشّح ليس عميلًا — اربطها بأحدهما لا بكليهما.',
    path: ['candidateId'],
  });
export type LinkConversationInput = z.infer<typeof LinkConversationSchema>;
